import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from './UserContext';
import { toast } from 'sonner';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [localFavorites, setLocalFavorites] = useState([]);
  const { user, isAuthenticated, favorites: serverFavorites, toggleFavorite: serverToggleFavorite } = useUser();

  // Use server favorites if logged in, otherwise use local storage
  const favorites = isAuthenticated ? serverFavorites : localFavorites;

  // Load local favorites from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('nishikigoi_favorites');
    if (stored) {
      try {
        setLocalFavorites(JSON.parse(stored));
      } catch (e) {
        console.error('Error loading favorites:', e);
        setLocalFavorites([]);
      }
    }
  }, []);

  // Save local favorites to localStorage whenever they change
  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem('nishikigoi_favorites', JSON.stringify(localFavorites));
    }
  }, [localFavorites, isAuthenticated]);

  const addFavorite = useCallback(async (varietyId) => {
    if (isAuthenticated) {
      const success = await serverToggleFavorite(varietyId);
      if (success) {
        toast.success('Ajouté aux favoris');
      }
      return success;
    } else {
      setLocalFavorites(prev => {
        if (prev.includes(varietyId)) return prev;
        return [...prev, varietyId];
      });
      toast.success('Ajouté aux favoris');
      return true;
    }
  }, [isAuthenticated, serverToggleFavorite]);

  const removeFavorite = useCallback(async (varietyId) => {
    if (isAuthenticated) {
      const success = await serverToggleFavorite(varietyId);
      if (success) {
        toast.success('Retiré des favoris');
      }
      return success;
    } else {
      setLocalFavorites(prev => prev.filter(id => id !== varietyId));
      toast.success('Retiré des favoris');
      return true;
    }
  }, [isAuthenticated, serverToggleFavorite]);

  const toggleFavorite = useCallback(async (varietyId) => {
    if (favorites.includes(varietyId)) {
      return removeFavorite(varietyId);
    } else {
      return addFavorite(varietyId);
    }
  }, [favorites, addFavorite, removeFavorite]);

  const isFavorite = useCallback((varietyId) => favorites.includes(varietyId), [favorites]);

  const clearFavorites = useCallback(() => {
    if (!isAuthenticated) {
      setLocalFavorites([]);
    }
  }, [isAuthenticated]);

  return (
    <FavoritesContext.Provider value={{
      favorites,
      addFavorite,
      removeFavorite,
      toggleFavorite,
      isFavorite,
      clearFavorites,
      favoritesCount: favorites.length,
      isAuthenticated
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export default FavoritesContext;
